import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';
import { ChevronLeft, ChevronRight, RefreshCw, Star, Film, User2, ChevronLeftCircle, AlertCircle, Popcorn, Check, Clock } from 'lucide-react';
import { getMovieRecommendations, type MovieRecommendation } from '../../../lib/openai';
import { WatchlistButton } from '../../ui/WatchlistButton';
import { WatchedButton } from '../../ui/WatchedButton';
import { useSearchLimits } from '../../../hooks/useSearchLimits';
import { ApiError } from '../../../lib/apiClient';

interface Props {
  selections: {
    path: 'genre' | 'mood' | '';
    genres: string[];
    subgenres: string[];
    favorites: string[];
    moods: string[];
    acclaimed: string;
    timeframes: string[];
  };
  onReset: () => void;
  onBack: () => void;
  showBackButton: boolean;
  onConsentRequired: () => void;
}

const MOVIES_PER_BATCH = 5;
const MAX_TOTAL_MOVIES = 15;

function formatRuntime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

export function MovieRecommendations({
  selections,
  onReset,
  onBack,
  showBackButton,
  onConsentRequired,
}: Props) {
  const { refreshUsage } = useSearchLimits();
  const [recommendations, setRecommendations] = useState<MovieRecommendation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [noMoreAvailable, setNoMoreAvailable] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const recommendationsRef = useRef<MovieRecommendation[]>([]);
  const initialLoadStartedRef = useRef(false);

  const loadInitialRecommendations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNoMoreAvailable(false);

      // Validate based on selected path
      if (selections.path === 'genre' && !selections.genres.length) {
        throw new Error('Please select at least one genre');
      }
      if (selections.path === 'mood' && !selections.moods.length) {
        throw new Error('Please select at least one mood');
      }

      // Server consumes one monthly pull before OpenAI.
      const movies = await getMovieRecommendations(
        selections.genres,
        selections.subgenres,
        selections.favorites,
        selections.moods,
        selections.acclaimed,
        selections.timeframes,
        [],
        MOVIES_PER_BATCH
      );

      if (!movies.length) {
        throw new Error('No movies found matching your criteria. Please try adjusting your preferences.');
      }

      recommendationsRef.current = movies;
      setRecommendations(movies);
      setCurrentIndex(0);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'AI_CONSENT_REQUIRED') {
        onConsentRequired();
        setError(null);
        return;
      }
      console.error('Error loading recommendations:', err);
      let errorMessage = 'Failed to load recommendations. Please try again.';

      if (err instanceof Error) {
        if (err.message.includes('API key')) {
          errorMessage = 'There was an issue with the AI service. Please try again later or contact support.';
        } else if (
          err.message.toLowerCase().includes('search limit') ||
          err.message.toLowerCase().includes('this month')
        ) {
          errorMessage = "You're out of recommendation pulls this month. Your allowance resets next month.";
        } else if (err.message.includes('rate limit')) {
          errorMessage = 'We\'re getting too many requests. Please wait a moment and try again.';
        } else if (err.message.includes('No movies found')) {
          errorMessage = err.message;
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
    } finally {
      await refreshUsage();
      setLoading(false);
    }
  }, [onConsentRequired, refreshUsage, selections]);

  useEffect(() => {
    if (initialLoadStartedRef.current) return;
    initialLoadStartedRef.current = true;
    void loadInitialRecommendations();
  }, [loadInitialRecommendations]);

  const loadMoreRecommendations = async () => {
    if (recommendations.length >= MAX_TOTAL_MOVIES || noMoreAvailable) return;

    try {
      setLoadingMore(true);
      setError(null);

      const slotsRemaining = MAX_TOTAL_MOVIES - recommendationsRef.current.length;
      if (slotsRemaining <= 0) {
        setNoMoreAvailable(true);
        return;
      }

      const batchSize = Math.min(MOVIES_PER_BATCH, slotsRemaining);
      const existingTitles = recommendationsRef.current.map(r => r.title);
      // Load-more is another OpenAI pull and counts toward the monthly allowance.
      const newMovies = await getMovieRecommendations(
        selections.genres,
        selections.subgenres,
        selections.favorites,
        selections.moods,
        selections.acclaimed,
        selections.timeframes,
        existingTitles,
        batchSize
      );

      if (!newMovies.length) {
        setNoMoreAvailable(true);
        return;
      }

      const cappedNewMovies = newMovies.slice(0, slotsRemaining);
      const updatedRecommendations = [...recommendationsRef.current, ...cappedNewMovies];
      recommendationsRef.current = updatedRecommendations;
      setRecommendations(updatedRecommendations);

      if (updatedRecommendations.length >= MAX_TOTAL_MOVIES) {
        setNoMoreAvailable(true);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'AI_CONSENT_REQUIRED') {
        onConsentRequired();
        return;
      }
      console.error('Error loading more recommendations:', err);
      setNoMoreAvailable(true);
    } finally {
      await refreshUsage();
      setLoadingMore(false);
    }
  };

  const handleNext = () => {
    if (currentIndex >= recommendations.length - 1) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev + 1);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handlePrevious = () => {
    if (currentIndex <= 0) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev - 1);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center space-y-8 p-8 py-16">
        <div className="space-y-2 text-center">
          <p className="eyebrow">Now curating</p>
          <h3 className="display text-2xl font-semibold">
            Finding your perfect movie match
          </h3>
          <p className="text-muted-foreground">
            Matching your taste against thousands of films…
          </p>
        </div>

        {/* Poster skeletons */}
        <div className="flex items-end gap-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={cn(
                'w-14 rounded-lg border border-white/[0.06] bg-secondary sm:w-16',
                i === 2 ? 'h-28 sm:h-32' : i === 1 || i === 3 ? 'h-24 sm:h-28' : 'h-20 sm:h-24'
              )}
              style={{
                animation: `float 2.2s ease-in-out ${i * 0.18}s infinite`,
                opacity: 0.5 + 0.15 * (2 - Math.abs(2 - i)),
              }}
            />
          ))}
        </div>

        <div className="h-1 w-56 overflow-hidden rounded-full bg-secondary">
          <div className="animate-progress h-full rounded-full bg-primary" />
        </div>
      </div>
    );
  }

  if (error && !recommendations.length) {
    return (
      <div className="space-y-6 py-6 text-center">
        <div className="flex items-center justify-center">
          <div className="flex max-w-md items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/10 p-5 text-left text-red-300">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        </div>

        <div className="flex justify-center gap-3">
          {showBackButton && (
            <button onClick={onBack} className="btn-ghost px-5 py-2.5 text-sm">
              <ChevronLeftCircle className="h-4 w-4" />
              Back
            </button>
          )}
          <button onClick={onReset} className="btn-marquee px-6 py-2.5 text-sm">
            Start Over
          </button>
        </div>
      </div>
    );
  }

  if (!recommendations.length) {
    return (
      <div className="space-y-6 py-6 text-center">
        <div className="flex items-center justify-center">
          <div className="flex max-w-md items-start gap-3 rounded-2xl border border-primary/25 bg-primary/10 p-5 text-left text-gold-soft">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <p>No movies found matching your criteria. Try adjusting your preferences.</p>
          </div>
        </div>

        <div className="flex justify-center gap-3">
          {showBackButton && (
            <button onClick={onBack} className="btn-ghost px-5 py-2.5 text-sm">
              <ChevronLeftCircle className="h-4 w-4" />
              Back
            </button>
          )}
          <button onClick={onReset} className="btn-marquee px-6 py-2.5 text-sm">
            Start Over
          </button>
        </div>
      </div>
    );
  }

  const currentMovie = recommendationsRef.current[currentIndex];
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < recommendations.length - 1;
  const showLoadMore = currentIndex === recommendations.length - 1 && recommendations.length < MAX_TOTAL_MOVIES && !noMoreAvailable;
  const batchNumber = Math.floor(recommendations.length / MOVIES_PER_BATCH);

  return (
    <div className="space-y-7">
      <div className="space-y-2 text-center">
        <p className="eyebrow">Tonight's lineup</p>
        <h2 className="display text-2xl font-semibold sm:text-3xl">
          Your personalized picks
        </h2>
        <p className="text-sm text-muted-foreground">
          Use the arrows to browse.
          {!noMoreAvailable && recommendations.length < MAX_TOTAL_MOVIES && ' Want more? Load another batch below.'}
        </p>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground sm:gap-8 sm:text-sm">
        <div className="flex items-center gap-2">
          <span className="rounded-full border border-primary/25 bg-primary/10 p-1.5 text-primary">
            <Popcorn className="h-4 w-4" />
          </span>
          Add to Watchlist
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full border border-emerald-400/25 bg-emerald-400/10 p-1.5 text-emerald-400">
            <Check className="h-4 w-4" />
          </span>
          Mark as Watched
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-primary/25 bg-primary/10 p-4 text-gold-soft">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <div className="relative">
        <div ref={carouselRef} className="overflow-hidden rounded-2xl border border-white/[0.08] bg-secondary/40">
          <div
            key={currentIndex}
            className={cn(
              'animate-rise transition-transform duration-300 ease-in-out',
              isTransitioning && 'transform'
            )}
          >
            <div className="flex flex-col md:flex-row">
              {/* Poster */}
              <div className="relative w-full p-5 md:w-[38%] md:p-6">
                <div
                  className="pointer-events-none absolute inset-6 -z-0 opacity-70 blur-2xl"
                  style={{ background: 'radial-gradient(50% 50% at 50% 45%, hsl(358 70% 42% / 0.22), transparent 70%)' }}
                />
                <div className="poster-frame relative w-full shadow-poster" style={{ paddingBottom: '150%' }}>
                  <div className="absolute inset-0 overflow-hidden rounded-xl">
                    {currentMovie.posterUrl ? (
                      <img
                        src={currentMovie.posterUrl}
                        alt={`${currentMovie.title} poster`}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/500x750?text=No+Poster';
                        }}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Film className="h-16 w-16 text-muted-foreground/40" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="w-full space-y-4 p-5 pt-1 md:w-[62%] md:p-7 md:pl-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="display text-2xl font-semibold leading-tight md:text-3xl">
                      {currentMovie.title}{' '}
                      <span className="font-normal text-muted-foreground">({currentMovie.year})</span>
                    </h3>
                    <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
                      {currentMovie.rating && (
                        <span className="rounded-md border border-white/15 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          {currentMovie.rating}
                        </span>
                      )}
                      {currentMovie.runtime && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          {formatRuntime(currentMovie.runtime)}
                        </span>
                      )}
                      {currentMovie.rottenTomatoesScore && (
                        <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                          <span aria-hidden="true">🍅</span>
                          {currentMovie.rottenTomatoesScore}%
                        </span>
                      )}
                      {currentMovie.imdbRating && (
                        <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                          <Star className="h-4 w-4 fill-primary text-primary" />
                          {currentMovie.imdbRating}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {currentMovie.tmdbId && (
                      <>
                        <WatchedButton
                          tmdbId={currentMovie.tmdbId}
                          title={currentMovie.title}
                          year={currentMovie.year}
                          posterUrl={currentMovie.posterUrl}
                          overview={currentMovie.description}
                        />
                        <WatchlistButton
                          tmdbId={currentMovie.tmdbId}
                          title={currentMovie.title}
                          year={currentMovie.year}
                          posterUrl={currentMovie.posterUrl}
                          overview={currentMovie.description}
                        />
                      </>
                    )}
                  </div>
                </div>

                <p className="leading-relaxed text-muted-foreground">{currentMovie.description}</p>

                <div className="flex items-start gap-2.5 text-sm">
                  <User2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground/60" />
                  <p className="text-muted-foreground">
                    <span className="font-medium text-foreground">{currentMovie.director}</span>
                    {currentMovie.cast.length > 0 && (
                      <>
                        {' · '}
                        {currentMovie.cast.join(', ')}
                      </>
                    )}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {currentMovie.genres.map((genre) => (
                    <span
                      key={genre}
                      className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-medium text-muted-foreground"
                    >
                      {genre}
                    </span>
                  ))}
                </div>

                {currentMovie.matchReason && (
                  <div className="rounded-xl border border-accent/20 bg-accent/[0.07] p-4">
                    <p className="text-sm leading-relaxed text-foreground/90">
                      <span className="font-semibold text-accent">Why it matches: </span>
                      {currentMovie.matchReason}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Carousel arrows */}
        <button
          onClick={handlePrevious}
          disabled={!canGoBack}
          aria-label="Previous movie"
          className={cn(
            'absolute left-0 top-1/2 hidden -translate-x-5 -translate-y-1/2 md:flex',
            'h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-popover text-foreground shadow-deep',
            'transition-all duration-200 hover:border-primary/50 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            !canGoBack && 'pointer-events-none opacity-30'
          )}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <button
          onClick={handleNext}
          disabled={!canGoForward}
          aria-label="Next movie"
          className={cn(
            'absolute right-0 top-1/2 hidden -translate-y-1/2 translate-x-5 md:flex',
            'h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-popover text-foreground shadow-deep',
            'transition-all duration-200 hover:border-primary/50 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            !canGoForward && 'pointer-events-none opacity-30'
          )}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Position dots + mobile arrows */}
      <div className="flex items-center justify-center gap-4" aria-label={`Movie ${currentIndex + 1} of ${recommendations.length}`}>
        <button
          onClick={handlePrevious}
          disabled={!canGoBack}
          aria-label="Previous movie"
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-popover text-foreground md:hidden',
            'transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            !canGoBack && 'pointer-events-none opacity-30'
          )}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-1.5">
          {recommendations.map((_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                i === currentIndex ? 'w-6 bg-primary' : 'w-1.5 bg-white/15'
              )}
            />
          ))}
        </div>
        <button
          onClick={handleNext}
          disabled={!canGoForward}
          aria-label="Next movie"
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-popover text-foreground md:hidden',
            'transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            !canGoForward && 'pointer-events-none opacity-30'
          )}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3">
        {showLoadMore && (
          <button
            onClick={loadMoreRecommendations}
            disabled={loadingMore}
            className="btn-marquee px-6 py-3"
          >
            <RefreshCw className={cn('h-5 w-5', loadingMore && 'animate-spin')} />
            {loadingMore ? 'Loading...' : `Get More Recommendations (Batch ${batchNumber + 1} of 3)`}
          </button>
        )}

        {noMoreAvailable && recommendations.length < MAX_TOTAL_MOVIES && (
          <div className="text-sm text-muted-foreground">
            No more movies available matching your criteria.
          </div>
        )}

        <button onClick={onReset} className="btn-ghost px-6 py-3">
          Start Over
        </button>
      </div>
    </div>
  );
}
